import { useEffect, useState } from 'react'
import { customerApi as api } from '../../services/api'

/**
 * Full-screen modal for an agent to record a lead collected during a
 * handover. Pre-fills from the currently open handover (visitor name,
 * handover_id) when supplied; otherwise opens blank for ad-hoc leads.
 *
 * Required: name + (email OR phone). Matches the bot's lead-capture
 * minimum so stats line up across the two sources.
 */
export default function AgentAddLeadModal({
  open,
  onClose,
  onSaved,
  prefill = {},
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [company, setCompany] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    if (!open) return
    // Reset / seed every time the modal opens so stale state from a
    // previous handover doesn't carry over.
    setName(prefill.visitor_name || '')
    setEmail(prefill.visitor_email || '')
    setPhone(prefill.phone || '')
    setCompany(prefill.company_name || '')
    setDescription(prefill.description || '')
    setErr('')
    setSubmitting(false)
  }, [open, prefill?.visitor_name, prefill?.visitor_email, prefill?.phone, prefill?.company_name, prefill?.description])

  if (!open) return null

  const minimumMet =
    (name || '').trim().length > 0 &&
    ((email || '').trim().length > 0 || (phone || '').trim().length > 0)

  const submit = async () => {
    if (!minimumMet) {
      setErr('Need a name AND at least one of email or phone.')
      return
    }
    setErr('')
    setSubmitting(true)
    try {
      const body = {
        visitor_name: name.trim(),
        visitor_email: (email || '').trim() || null,
        phone: (phone || '').trim() || null,
        company_name: (company || '').trim() || null,
        description: (description || '').trim() || null,
        handover_id: prefill.handover_id || null,
      }
      const r = await api.post('/agent/leads', body)
      onSaved?.(r.data)
      onClose?.()
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        'Could not save the lead. Please try again.'
      setErr(typeof msg === 'string' ? msg : 'Validation error — check the fields and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Add lead
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {prefill.handover_id
                ? 'From this handover — pre-filled with the visitor info'
                : 'Record a new lead you collected manually'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {/* Body — scrolls on small screens */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <Field
            label="Name"
            required
            value={name}
            onChange={setName}
            placeholder="e.g. Priya Rao"
            autoFocus
          />
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="e.g. priya@example.com"
          />
          <Field
            label="Phone"
            value={phone}
            onChange={setPhone}
            placeholder="e.g. +91 98765 43210"
          />
          <Field
            label="Company"
            value={company}
            onChange={setCompany}
            placeholder="e.g. Cognavia Pvt Ltd"
          />
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="What were they interested in? Any next-step context the admin should know."
              className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Short notes save best — this shows on the lead's detail view.
            </p>
          </div>

          {err && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-xs px-3 py-2">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-3.5 border-t border-slate-200 bg-slate-50">
          <p className="text-[11px] text-slate-400">
            Required: name + email OR phone.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-3.5 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || !minimumMet}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Saving…' : 'Save lead'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Field({ label, required, value, onChange, type = 'text', placeholder, autoFocus }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
      />
    </div>
  )
}
